/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState, useContext } from "react";
import Loading from "@/common/components/ui/loading/Loading";
import { getRenterCollectors } from "@/common/services/renters.api";
import { useCompanyStore } from "@/common/states/store/Company";
import {
  TableFilters,
  TableFiltersState,
} from "@/common/components/ui/table/TableFilters";
import GenericTable from "@/common/components/ui/table/GenericTable";
import TableFooter from "@/common/components/ui/table/TableFooter";
import { AuthContext } from "@/common/components/auth/authContext/AuthContext";
import ModalCreateCollector from "@/modules/leads/components/modals/CreateCollectors";
import { ILead } from "@/types/lead.type";
import { IRenter } from "@/types/renter.type";

const EMPTY_ACTION = { eventName: "", data: undefined };

function CollectorsPage() {
  const { auth } = useContext(AuthContext);
  const { selectedCompanies, filterMode } = useCompanyStore();
  const [collectors, setCollectors] = useState<IRenter[]>([]);
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState<{ eventName: string; data?: unknown }>(
    EMPTY_ACTION
  );

  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "",
    orderBy: "-updatedAt",
    itemsPerPage: "10",
  });

  const [page, setPage] = useState<number>(1);
  const [paginatedCollectors, setPaginatedCollectors] = useState<any[]>([]);

  const headers = {
    name: "Nome",
    cpf: "CPF",
    whatsapp: "whatsapp",
    pix: "Chave Pix",
  };

  useEffect(() => {
    async function fetchCollectors() {
      setLoading(true);
      try {
        const response = await getRenterCollectors();
        if (response?.data) {
          const sorted = response.data.sort((a, b) =>
            a.name.localeCompare(b.name)
          );
          setCollectors(sorted);
        }
      } finally {
        setLoading(false);
      }
    }
    fetchCollectors();
  }, [filterMode, selectedCompanies]);

  useEffect(() => {
    const searchText = filters.search?.toLowerCase() ?? "";
    const filtered = collectors.filter((collector) => {
      return (
        collector?.name?.toLowerCase().includes(searchText) ||
        collector?.cpf?.includes(searchText) ||
        collector?.whatsapp?.toLowerCase().includes(searchText)
      );
    });

    const startIndex = (page - 1) * Number(filters.itemsPerPage);
    const endIndex = startIndex + Number(filters.itemsPerPage);

    setPaginatedCollectors(filtered.slice(startIndex, endIndex));
  }, [filters, collectors, page]);

  const isAction = (eventName: string) => action.eventName === eventName;

  const cleanActionData = () => {
    setAction(EMPTY_ACTION);
  };

  function openConfirmEdit(leadData: ILead) {
    setAction({ eventName: "create", data: leadData });
  }

  const totalItems = collectors.filter((collector) => {
    const searchText = filters.search?.toLowerCase() ?? "";
    return (
      collector?.name?.toLowerCase().includes(searchText) ||
      collector?.cpf?.includes(searchText) ||
      collector?.whatsapp?.toLowerCase().includes(searchText)
    );
  }).length;

  // Função para normalizar o tipo de chave Pix
  function normalizeKeyType(keytype?: string) {
    if (!keytype) return "-";
    if (
      keytype.toLowerCase().includes("telefone") ||
      keytype.toLowerCase().includes("número")
    ) {
      return "Telefone";
    }
    return keytype;
  }

  return (
    <div className="table-section">
      <TableFilters
        dashboardHeader="Recolhedores"
        placeholder="Buscar por nome, CPF ou e-mail"
        newItemLabel="Adicionar"
        onNewItemClick={() =>
          setAction({ eventName: "create", data: undefined })
        }
        onSearch={(f) => {
          setFilters((prev) => ({
            ...prev,
            search: f.search,
            status: f.status,
          }));
          setPage(1);
        }}
        entity="collectors"
        tableData={collectors}
        statusOptions={{}}
      />

      {loading ? (
        <Loading />
      ) : (
        <GenericTable
          headers={headers}
          data={paginatedCollectors}
          setFilters={({ orderBy }) =>
            setFilters((prev) => ({ ...prev, orderBy }))
          }
          renders={{
            name: (name) => (
              <span className="font-semibold">{String(name)}</span>
            ),
            cpf: (cpf) => <span>{String(cpf)}</span>,
            email: (whatsapp) => <span>{String(whatsapp)}</span>,
            pix: (_: unknown, context: unknown) => {
              const row = context as IRenter;
              return (
                <span>
                  {normalizeKeyType(row.dataPix?.keytype)} -{" "}
                  {row.dataPix?.key || "-"}
                </span>
              );
            },
          }}
          entity="collectors"
        />
      )}

      <TableFooter
        page={page}
        totalItems={totalItems}
        itemsPerPage={Number(filters.itemsPerPage)}
        goToPage={setPage}
      />

      {isAction("create") && (
        <ModalCreateCollector
          open={true}
          roles={auth?.user.roles}
          openConfirmEdit={openConfirmEdit}
          onClose={cleanActionData}
          onSave={async () => {
            const updated = await getRenterCollectors();
            if (updated?.data) setCollectors(updated.data);
          }}
        />
      )}
    </div>
  );
}

export default CollectorsPage;
